import React, { useState, ReactNode } from 'react';
import { WithStyles, createStyles, withStyles } from '@indigo-ag/component-module';
import {
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  TableCellProps
} from '@indigo-ag/component-module/dist/esm/_deprecated';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import TablePagination from '@material-ui/core/TablePagination';

import { EnhancedPaper } from '../EnhancedPaper/EnhancedPaper';
import i18next from 'core/i18n';

export type CellMapperType = string | number | ReactNode;

export interface HeadCell<T> {
  id: keyof T;
  fullWidth?: boolean;
  label: string;
  unSort?: boolean;
  align?: TableCellProps['align'];
  mapper?: (data: T) => CellMapperType;
}

export interface EnhancedTablePagination {
  totalCount: number;
  rowsPerPage: number;
  page: number;
  handleChangePage: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
  handleChangeRowsPerPage?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  rowsPerPageOptions?: number[];
}

interface EnhancedTableProps extends WithStyles<typeof styles> {
  rows: any[];
  headCells: HeadCell<any>[];
  initOrderBy?: string;
  onRowClick?: (event: React.MouseEvent, row: unknown) => void;
  hideHeader?: boolean;
  sort?: boolean;
  ignorePaper?: boolean;
  verticalAlignBaselineRows?: boolean;
  pagination?: EnhancedTablePagination;
}

export enum SortType {
  ASC = 'asc',
  DESC = 'desc'
}

type Order = SortType.ASC | SortType.DESC;

const styles = () =>
  createStyles({
    headerLabel: {
      color: '#5B6B72',
      fontSize: '0.813rem',
      whiteSpace: 'pre'
    },
    customTable: {
      '& .MuiTableCell-root': {
        padding: '14px'
      },
      '& .MuiTableCell-body': {
        fontWeight: 'normal'
      }
    },
    hide: {
      display: 'none'
    },
    rowClick: {
      cursor: 'pointer'
    },
    sortIcon: {
      color: '#969696 !important'
    },
    tableWrapper: {
      width: '100%',
      boxShadow: '0px 2px 4px rgba(0, 87, 125, 0.14), 0px 2px 2px rgba(0, 87, 125, 0.1)'
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1
    },
    verticalBaseline: {
      verticalAlign: 'baseline'
    }
  });

function stableSort<T>(array: T[], cmp: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function desc<T>(a: T, b: T, orderBy: keyof T) {
  if (a[orderBy] === null || a[orderBy] === undefined) {
    return 1;
  } else if (b[orderBy] === null || b[orderBy] === undefined) {
    return -1;
  } else if (b[orderBy] < a[orderBy]) {
    return 1;
  } else if (b[orderBy] > a[orderBy]) {
    return -1;
  }
  return 0;
}

function getSorting<K extends keyof any>(
  order: Order,
  orderBy: K
): (a: { [key in K]: number | string }, b: { [key in K]: number | string }) => number {
  return order === SortType.DESC ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

// TODO Remove this component after all tables migrated to design system.
const EnhancedTableComponent: React.FunctionComponent<EnhancedTableProps> = (props: EnhancedTableProps) => {
  const {
    classes,
    rows,
    headCells,
    initOrderBy,
    onRowClick,
    hideHeader,
    sort = false,
    ignorePaper = false,
    verticalAlignBaselineRows = false
  } = props;

  /**
   * State
   */
  const [order, setOrder] = useState<Order>(SortType.ASC);
  const [orderBy, setOrderBy] = useState(initOrderBy ? initOrderBy : headCells[0].id);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    setOrder(order !== SortType.ASC ? SortType.ASC : SortType.DESC);
    setOrderBy(property);
  };

  const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
    handleRequestSort(event, property);
  };

  const rowsToRender = rows && rows.length ? (sort ? stableSort(rows, getSorting(order, orderBy)) : rows) : [];

  const TableEl = (
    <>
      <Table aria-labelledby="tableTitle" aria-label="enhanced table" className={classes.customTable}>
        <TableHead className={hideHeader ? classes.hide : ''}>
          <TableRow>
            {headCells.map(headCell =>
              sort && !headCell.unSort ? (
                <TableCell
                  key={headCell.id.toString()}
                  align={headCell.align ? headCell.align : 'left'}
                  sortDirection={orderBy === headCell.id ? order : false}
                  style={{ width: headCell.fullWidth ? '100%' : 'auto' }}
                >
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={order}
                    onClick={createSortHandler(headCell.id)}
                    classes={{ root: classes.headerLabel, icon: classes.sortIcon }}
                  >
                    {headCell.label}
                    {orderBy === headCell.id ? (
                      <span className={classes.visuallyHidden} data-test="head-cell-sorting">
                        {order === SortType.DESC ? 'sorted descending' : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ) : (
                <TableCell key={headCell.id.toString()}>{headCell.label}</TableCell>
              )
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rowsToRender.length ? (
            rowsToRender.map((row, index) => {
              return (
                <TableRow
                  tabIndex={-1}
                  key={row.id ? row.id : index}
                  onClick={onRowClick ? event => onRowClick(event, row) : undefined}
                  className={`${verticalAlignBaselineRows ? classes.verticalBaseline : ''} ${
                    onRowClick ? classes.rowClick : ''
                  }`}
                >
                  {headCells.map((headCell, headCellIndex) => (
                    <TableCell align={headCell.align ? headCell.align : 'left'} key={`${index}-${headCellIndex}`}>
                      {headCell.mapper ? headCell.mapper(row) : row[headCell.id]}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          ) : (
            <TableRow tabIndex={-1}>
              <TableCell align="center" colSpan={headCells && headCells.length ? headCells.length : 1}>
                {i18next.t('noResultsFound')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {props.pagination ? (
        <TablePagination
          rowsPerPageOptions={props.pagination.rowsPerPageOptions || [5, 10, 20]}
          component="div"
          count={props.pagination.totalCount}
          rowsPerPage={props.pagination.rowsPerPage}
          page={props.pagination.page}
          onChangePage={props.pagination.handleChangePage}
          onChangeRowsPerPage={props.pagination.handleChangeRowsPerPage}
        />
      ) : null}
    </>
  );

  return ignorePaper ? (
    TableEl
  ) : (
    <EnhancedPaper padding="16px 24px 40px 24px" className={classes.tableWrapper}>
      {TableEl}
    </EnhancedPaper>
  );
};

export const EnhancedTable = withStyles(styles)(EnhancedTableComponent);
